/** @format */

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { colors } from "../../../utility/types/constant";

const FONT = "'Montserrat', sans-serif";
const MAX_REASON_LENGTH = 500;

interface RejectInvitationDialogProps {
	open: boolean;
	inviteeName: string;
	circleName: string;
	onCancel: () => void;
	onConfirm: (reason: string) => void;
	onExited?: () => void;
}

/**
 * Admin-only confirmation for rejecting an invitation, with an optional reason
 * that is shared with the invitee and the inviter.
 */
const RejectInvitationDialog = ({
	open,
	inviteeName,
	circleName,
	onCancel,
	onConfirm,
	onExited,
}: RejectInvitationDialogProps) => {
	const [reason, setReason] = useState("");

	useEffect(() => {
		if (open) setReason("");
	}, [open]);

	return (
		<Dialog
			open={open}
			onClose={onCancel}
			TransitionProps={{ onExited }}
			fullWidth
			maxWidth="xs"
			PaperProps={{ sx: { borderRadius: "20px", m: 2 } }}>
			<DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, pb: 1 }}>
				Reject this invitation?
			</DialogTitle>
			<DialogContent>
				<Typography
					sx={{ fontFamily: FONT, color: "text.secondary", lineHeight: 1.6, mb: 2 }}>
					<strong>{inviteeName}</strong> won't be able to join{" "}
					<strong>{circleName}</strong> with this invitation. The invitee and the
					person who sent it will be notified.
				</Typography>
				<TextField
					label="Reason (optional)"
					placeholder="Shared with the invitee and inviter"
					value={reason}
					onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
					multiline
					minRows={2}
					fullWidth
					helperText={`${reason.length}/${MAX_REASON_LENGTH}`}
					InputProps={{ sx: { borderRadius: "12px", fontFamily: FONT } }}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
				<Button
					onClick={onCancel}
					sx={{
						minHeight: 44,
						borderRadius: "12px",
						textTransform: "none",
						fontFamily: FONT,
						fontWeight: 700,
						color: "text.secondary",
					}}>
					Cancel
				</Button>
				<Button
					variant="contained"
					disableElevation
					onClick={() => onConfirm(reason.trim())}
					sx={{
						minHeight: 44,
						borderRadius: "12px",
						textTransform: "none",
						fontFamily: FONT,
						fontWeight: 700,
						backgroundColor: colors.persianRed,
						"&:hover": { backgroundColor: "#b02b2a" },
					}}>
					Reject
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default RejectInvitationDialog;
